import {
  useEffect,
  useContext,
  useState,
  type ComponentType,
  type Dispatch,
  type SetStateAction
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import keywordPlannerApi, {
  useGetHistoryPlannerQuery,
  useDeleteHistoryPlannerMutation
} from '@/app/KeywordResearch/api/keywordPlannerApi';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { plannerHistoryColumns } from '@/app/KeywordResearch/config/plannerHistoryColumns';
import { RemovePlannerContext } from '@/context/Context';
import { useDispatch } from 'react-redux';
import { successMessage, errorMessage } from '@/utils/toast';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import NoKeywordPlannerData from '../components/keyword-planner/NoKeywordPlannerData';
import NewPlannerForm from '../components/keyword-planner/NewPlannerForm';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import geoLanguagesData from '@/app/common/api/backups/languages.json';
import geoLocationsData from '@/app/common/api/backups/locations.json';
import CancelModal from '@/app/common/components/CancelModal';
import IconAlert from '@/components/common/IconAlert';

type BreadcrumbItem = { text: string; href: string };
type OutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type AgGridWrapperProps = {
  columns: unknown;
  data?: unknown;
  height: string;
};

type AgGridSkeletonProps = {
  height: string;
};

type NewPlannerFormProps = {
  onInputTypeChange?: (type: string) => void;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;
const TypedAgGridSkeleton = AgGridSkeleton as ComponentType<AgGridSkeletonProps>;
const TypedNewPlannerForm = NewPlannerForm as ComponentType<NewPlannerFormProps>;

const isLoadingGeoLanguages = false;
const isLoadingGeoLocations = false;

const Planner = () => {
  const [selectedInputType, setSelectedInputType] = useState('keyword');
  const dispatch = useDispatch();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const access = useToolAccess('keyword_planner');
  const { data: userInfo } = useGetUserInfoQuery();
  const [deleteHistory] = useDeleteHistoryPlannerMutation();

  const {
    taskId: remTaskId,
    keyword: remKeryword,
    show: showRemModal,
    setShow: setShowRemModal
  } = useContext(RemovePlannerContext);

  useEffect(() => {
    setBreadcrumbData([{ text: 'کیورد پلنر', href: '#' }]);
  }, [setBreadcrumbData]);

  const { data, isFetching, isError, error, refetch } = useGetHistoryPlannerQuery(
    {
      requested_user: {
        name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
        email: userInfo?.data?.email
      }
    },
    { skip: access.isLoading || !access.haveAccess }
  );
  if (access.isLoading) return null;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار کیورد پلنر" />;
  }

  const queryError = getActiveQueryError([
    { isError, error, refetch, isFetching },
    {
      isError: access.isError,
      error: access.error,
      refetch: access.refetch,
      isFetching: access.isFetching
    }
  ]);

  if (queryError) return <DataLoadError {...queryError} />;
  if (!isFetching && !data?.data) return <NoKeywordPlannerData />;

  return (
    <>
      <TypedNewPlannerForm onInputTypeChange={setSelectedInputType} />
      {selectedInputType === 'url' && (
        <IconAlert variant="warning">
          <strong>هشدار عدم دقت:</strong> در نظر داشته باشید که داده‌های گوگل
          ادز ممکنه برای آدرس ارسالی دقیق نباشه و این مورد خارج از کنترل میزفا
          تولز است (منبع این بخش گوگل ادز می‌باشد).
        </IconAlert>
      )}
      <Row>
        <Col>
          {!isFetching &&
          !isLoadingGeoLanguages &&
          !isLoadingGeoLocations &&
          !access.isLoading ? (
            <div className="for-style">
              <TypedAgGridWrapper
                columns={plannerHistoryColumns(
                  geoLocationsData.data,
                  geoLanguagesData.data
                )}
                data={data?.data}
                height="700px"
              />
            </div>
          ) : (
            <TypedAgGridSkeleton height="650px" />
          )}
          <style>
            {`
                .for-style .ag-row-focus {
                    z-index: 2;
                }`}
          </style>
        </Col>
      </Row>
      <CancelModal
        setShow={setShowRemModal}
        show={showRemModal}
        submitTitle="آره، حذف بشه"
        title="حذف اطلاعات کیورد پلنر"
        submitFunc={() => {
          deleteHistory({
            task_id: remTaskId
          })
            .then(() => {
              successMessage('اطلاعات با موفقیت حذف شد');
              dispatch(
                keywordPlannerApi.util.invalidateTags(['PlannerHistory'])
              );
            })
            .catch(() => {
              errorMessage('در حذف اطلاعات خطایی رخ داده');
            });
        }}
        desc={
          <>
            آیا مطمئن هستید میخواهید اطلاعات{' '}
            <b>&nbsp;{decodeURIComponent(remKeryword)}</b> را حذف کنید؟
          </>
        }
      />
    </>
  );
};

export default Planner;
